import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Package,
  Hash,
  Tag,
  DollarSign,
  FileText,
  Star,
  Loader2,
  Upload,
  Image as ImageIcon,
  CheckCircle,
  FolderOpen,
  AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { supabase, getImageUrl } from '../../lib/supabase';
import { useAdminCategories, useUpdateProduct } from '../../services/adminService';
import { useUploadMedia, validateFile, formatFileSize } from '../../services/mediaService';
import { MediaLibraryModal } from '../MediaLibraryModal/MediaLibraryModal';

const overlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const panel = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } },
  exit: { x: '100%', transition: { duration: 0.2 } },
};

/**
 * Slide-over panel for editing an existing product.
 * @param {{ product: object, onClose: () => void }} props
 */
export function EditProductSlideOver({ product, onClose }) {
  const { data: categories } = useAdminCategories();
  const updateMutation = useUpdateProduct();
  const uploadMutation = useUploadMedia();

  const [form, setForm] = useState({
    title: '',
    slug: '',
    description: '',
    short_description: '',
    price: '',
    category_id: '',
    is_featured: false,
    feature_priority: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [imageError, setImageError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);

  // Populate form when product appears
  useEffect(() => {
    if (product) {
      setForm({
        title: product.title || '',
        slug: product.slug || '',
        description: product.description || '',
        short_description: product.short_description || '',
        price: product.price?.toString() || '',
        category_id: product.category_id || '',
        is_featured: product.is_featured || false,
        feature_priority: product.feature_priority?.toString() || '',
      });
      setImageFile(null);
      setImagePreview(null);
      setImagePath('');
      setImageError('');
      setUploading(false);
      setUploadProgress(0);
      setSuccess(false);
      setMediaModalOpen(false);
    }
  }, [product]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Auto-generate slug from title
  const handleTitleChange = (val) => {
    update('title', val);
    update(
      'slug',
      val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
    );
  };

  // Handle image selection with validation
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError('');
    const check = validateFile(file);
    if (!check.valid) {
      setImageError(check.error);
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImagePath('');
  };

  // Handle image from media library
  const handleMediaSelect = (media) => {
    setImageFile(null);
    setImagePreview(media.url);
    setImagePath(media.file_path);
    setImageError('');
    setMediaModalOpen(false);
  };

  // Close handler via Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Price change preview
  const priceChanged = product && Number(form.price) !== Number(product.price);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);

    const updates = {
      title: form.title,
      slug: form.slug,
      description: form.description || null,
      short_description: form.short_description || null,
      price: Number(form.price),
      category_id: form.category_id || null,
      is_featured: form.is_featured,
      feature_priority: form.feature_priority ? Number(form.feature_priority) : null,
    };

    // Upload image if changed
    if (imageFile) {
      setUploading(true);
      setUploadProgress(0);
      try {
        const result = await uploadMutation.mutateAsync({
          file: imageFile,
          productId: product.id,
          onProgress: (pct) => setUploadProgress(pct),
        });
        updates.image_path = result.path;
      } catch (uploadErr) {
        setUploading(false);
        throw uploadErr;
      }
      setUploading(false);
    } else if (imagePath) {
      updates.image_path = imagePath;
    }

    updateMutation.mutate(
      { id: product.id, updates },
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => onClose(), 1200);
        },
      }
    );
  };

  if (!product) return null;

  const currentImage = imagePreview || (imagePath ? getImageUrl(imagePath) : getImageUrl(product.image_path));

  return (
    <AnimatePresence>
      {product && (
        <>
          {/* Backdrop */}
          <motion.div
            key="edit-backdrop"
            variants={overlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-40 bg-brand-dark/40 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.div
            key="edit-panel"
            variants={panel}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-brand-white shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border flex-shrink-0">
              <div>
                <h2 className="text-lg font-extrabold text-brand-dark">Edit Product</h2>
                <p className="text-xs text-brand-gray mt-0.5">Update details for {product.title}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Image */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Product Image
                </label>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-brand-light border border-ui-border flex-shrink-0">
                    {currentImage ? (
                      <img
                        src={currentImage}
                        alt={form.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={24} className="text-ui-muted" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl cursor-pointer transition-colors">
                        <Upload size={14} />
                        {imageFile ? 'Change' : 'Replace'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setMediaModalOpen(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                      >
                        <FolderOpen size={14} />
                        Library
                      </button>
                    </div>

                    {imageFile && !uploading && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-brand-gray truncate max-w-[120px]">
                          {imageFile.name}
                        </span>
                        <span className="text-[10px] text-ui-muted">
                          ({formatFileSize(imageFile.size)})
                        </span>
                      </div>
                    )}

                    {imagePath && !imageFile && (
                      <span className="flex items-center gap-1 text-xs text-success font-medium">
                        <CheckCircle size={12} />
                        From library
                      </span>
                    )}

                    {/* Upload progress */}
                    {uploading && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin text-brand-red" />
                          <span className="text-xs font-medium text-brand-gray">
                            Uploading… {uploadProgress}%
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-brand-light rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadProgress}%` }}
                            className="h-full bg-brand-red rounded-full"
                            transition={{ duration: 0.2 }}
                          />
                        </div>
                      </div>
                    )}

                    <p className="text-[10px] text-ui-muted">Max 5 MB · JPG, PNG, WebP, GIF, SVG</p>
                    {imageError && (
                      <div className="flex items-center gap-1 text-xs text-error">
                        <AlertCircle size={12} />
                        {imageError}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Product Name
                </label>
                <div className="relative">
                  <Package
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted"
                  />
                  <input
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                </div>
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Slug</label>
                <div className="relative">
                  <Hash
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted"
                  />
                  <input
                    type="text"
                    required
                    value={form.slug}
                    onChange={(e) => update('slug', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                </div>
              </div>

              {/* Category + Price row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Category
                  </label>
                  <div className="relative">
                    <Tag
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted"
                    />
                    <select
                      value={form.category_id}
                      onChange={(e) => update('category_id', e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 appearance-none"
                    >
                      <option value="">Select category</option>
                      {(categories || []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Price (KES)
                  </label>
                  <div className="relative">
                    <DollarSign
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted"
                    />
                    <input
                      type="number"
                      required
                      min="0"
                      value={form.price}
                      onChange={(e) => update('price', e.target.value)}
                      className={clsx(
                        'w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted',
                        priceChanged ? 'border-warning' : 'border-ui-border'
                      )}
                    />
                  </div>
                  {priceChanged && (
                    <p className="text-xs text-warning mt-1 font-medium">
                      Price changed from KES {Number(product.price).toLocaleString()} →{' '}
                      {Number(form.price).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Featured row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer py-2.5">
                    <input
                      type="checkbox"
                      checked={form.is_featured}
                      onChange={(e) => update('is_featured', e.target.checked)}
                      className="w-4 h-4 rounded border-ui-border text-brand-red focus:ring-brand-red/20"
                    />
                    <Star size={16} className="text-brand-red" />
                    <span className="text-sm font-medium text-brand-dark">Featured</span>
                  </label>
                </div>
              </div>

              {/* Short description */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Short Description
                </label>
                <input
                  type="text"
                  value={form.short_description}
                  onChange={(e) => update('short_description', e.target.value)}
                  placeholder="A brief tagline..."
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                />
              </div>

              {/* Full description */}
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Full Description
                </label>
                <div className="relative">
                  <FileText size={16} className="absolute left-3 top-3 text-ui-muted" />
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    placeholder="Detailed description of the product..."
                    className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted resize-none"
                  />
                </div>
              </div>
            </form>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 bg-brand-white flex-shrink-0">
              {success ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center gap-2 text-success font-bold text-sm"
                >
                  <CheckCircle size={18} />
                  Saved successfully!
                </motion.div>
              ) : (
                <>
                  <button
                    type="submit"
                    form={undefined}
                    onClick={(e) => {
                      // Programmatic submit via the form
                      e.currentTarget
                        .closest('.fixed')
                        .querySelector('form')
                        .requestSubmit();
                    }}
                    disabled={updateMutation.isPending}
                    className={clsx(
                      'flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all',
                      updateMutation.isPending
                        ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                        : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg'
                    )}
                  >
                    {updateMutation.isPending && (
                      <Loader2 size={16} className="animate-spin" />
                    )}
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}

              {updateMutation.isError && (
                <p className="text-xs text-error ml-auto">
                  {updateMutation.error?.message || 'Failed to save'}
                </p>
              )}
            </div>
          </motion.div>

          {/* Media library modal */}
          <MediaLibraryModal
            open={mediaModalOpen}
            onClose={() => setMediaModalOpen(false)}
            onSelect={handleMediaSelect}
            productId={product?.id}
            selectMode
          />
        </>
      )}
    </AnimatePresence>
  );
}
